import { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "wouter";

interface User {
    id: number;
    username: string;
    isAdmin: boolean;
    voteCount: number;
    email: string;
    nameId: string | null;
    displayName: string | null;
    profilePicture: string | null;
    authType: string;
    lastLogin: string | null;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    login: () => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        try {
            const res = await fetch("/api/me", {
                credentials: "include",
            });
            if (res.ok) {
                const userData = await res.json();
                setUser(userData);
            }
        } catch (error) {
            // Remove console.error statements
        } finally {
            setIsLoading(false);
        }
    };

    const login = () => {
        // Redirect to Microsoft SSO login
        window.location.href = '/api/auth/microsoft';
    };

    const logout = () => {
        // Always use the SSO logout endpoint
        window.location.href = '/api/auth/microsoft/logout';
    };

    return (
        <AuthContext.Provider value={{ user, isLoading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
} 